import React from 'react';
import { Box, Button, Grid } from '@mui/material';
import { Form } from 'formik';
import { LocationOpeningHoursCardComponent } from 'components/location/cards/LocationOpeningHoursCardComponent';
import { DAYS_OF_THE_WEEK, Location, LocationOpeningHours } from 'components/location/models/LocationModel';
import { MuiForm } from 'lib/Form';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { Row } from 'ui/Flex';
import { Panel } from 'ui/Panel';
import { locationApi } from '../LocationApi';
import { LocationOpeningHoursEditForm } from '../forms/LocationOpeningHoursEditForm';

interface LocationOpeningHoursCardProps {
    currentLocation: Location;
    setCurrentLocation: (location: Location) => void;
}

interface RequestBody {
    openingHours?: LocationOpeningHours;
    alternativeOpeningHoursText?: string;
}

type OpeningHoursMode = 'STANDART' | 'HTML';

interface HTMLOpeningHoursFormValues {
    alternativeOpeningHoursText: string;
}

export const LocationOpeningHoursCard: React.FC<LocationOpeningHoursCardProps> = ({
    currentLocation,
    setCurrentLocation
}) => {
    const [isEditing, setIsEditing] = React.useState<OpeningHoursMode | null>(null);

    const htmlInitialValues: HTMLOpeningHoursFormValues = React.useMemo(
        () => ({
            alternativeOpeningHoursText: currentLocation.alternativeOpeningHoursText ?? ''
        }),
        [currentLocation.alternativeOpeningHoursText]
    );

    const handleStandardSubmit = React.useCallback(
        async (data: LocationOpeningHours) => {
            const locationId = currentLocation._id;
            const body: RequestBody = {
                openingHours: DAYS_OF_THE_WEEK.reduce((acc, day) => {
                    acc[day] = data[day];
                    return acc;
                }, {} as LocationOpeningHours)
            };

            const response = await locationApi.update(locationId, { body });
            if (response.ok) {
                setCurrentLocation(response.body);
            }
            setIsEditing(null);
        },
        [currentLocation._id, setCurrentLocation]
    );

    const handleHTMLSubmit = React.useCallback(
        async (values: HTMLOpeningHoursFormValues) => {
            const locationId = currentLocation._id;
            const body: RequestBody = {
                alternativeOpeningHoursText: values.alternativeOpeningHoursText
            };

            const response = await locationApi.update(locationId, { body });
            if (response.ok) {
                setCurrentLocation(response.body);
            }
            setIsEditing(null);
        },
        [currentLocation._id, setCurrentLocation]
    );

    const toggleState = React.useCallback(
        (type: OpeningHoursMode) => () => {
            setIsEditing(current => (type === current ? null : type));
        },
        []
    );

    return (
        <>
            <Box marginBottom={2}>
                <Panel
                    title="Opening Hours"
                    showAction={!isEditing}
                    onClick={toggleState('STANDART')}
                    divider
                >
                    {isEditing === 'STANDART' ? (
                        <LocationOpeningHoursEditForm
                            onSubmit={handleStandardSubmit}
                            onClose={toggleState('STANDART')}
                            location={currentLocation}
                        />
                    ) : (
                        <LocationOpeningHoursCardComponent mode="STANDART" location={currentLocation} />
                    )}
                </Panel>
            </Box>
            <Box marginTop={2}>
                <Panel
                    title="Custom opening hours"
                    showAction={!isEditing}
                    onClick={toggleState('HTML')}
                    divider
                >
                    {isEditing === 'HTML' ? (
                        <MuiForm<HTMLOpeningHoursFormValues>
                            initialValues={htmlInitialValues}
                            onSubmit={handleHTMLSubmit}
                            enableReinitialize
                        >
                            {({ submitForm, isSubmitting }) => (
                                <Form>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextFormField
                                                name="alternativeOpeningHoursText"
                                                label="Custom Opening Hours HTML"
                                                multiline
                                                rows={4}
                                                description={
                                                    'Using HTML, This lets you override how opening hours ' +
                                                    'are displayed in the app. However, the standard opening ' +
                                                    'hours will still be used to determine when the location ' +
                                                    'is open for ordering. This will replace your standard ' +
                                                    'defined opening hours.'
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Row gutter align="flex-end">
                                                <Button onClick={toggleState('HTML')} variant="outlined">
                                                    Cancel
                                                </Button>
                                                <Button
                                                    onClick={submitForm}
                                                    disabled={isSubmitting}
                                                    variant="contained"
                                                    color="primary"
                                                >
                                                    Submit
                                                </Button>
                                            </Row>
                                        </Grid>
                                    </Grid>
                                </Form>
                            )}
                        </MuiForm>
                    ) : (
                        <LocationOpeningHoursCardComponent mode="HTML" location={currentLocation} />
                    )}
                </Panel>
            </Box>
        </>
    );
};
